import type { DrivingLimitStatus } from '../../../api/apiService';
import { formatClock, formatDrivingDuration, formatLimitHours, isDrivingLimitStale } from '../../../lib/drivingLimit';
import type {
  FourMonthAverageLimit,
  LimitKey,
  LimitLevel,
  WeeklyDrivingLimit,
  DriverLimits,
} from './types';

type Translate = (key: string, options?: Record<string, unknown>) => string;

export const LIMIT_WARNING_RATIO = 0.8;
export const LIMIT_CRITICAL_RATIO = 0.95;

const LEVEL_RANK: Record<LimitLevel, number> = { unknown: 0, ok: 1, warning: 2, critical: 3, reached: 4 };

export const levelForRatio = (ratio: number): LimitLevel => {
  if (!Number.isFinite(ratio)) return 'unknown';
  if (ratio >= 1) return 'reached';
  if (ratio >= LIMIT_CRITICAL_RATIO) return 'critical';
  if (ratio >= LIMIT_WARNING_RATIO) return 'warning';
  return 'ok';
};

/** Same visibility rules as DrivingLimitNotice (ALL-41): the server status decides. */
export const rolling24hLevel = (limit: DrivingLimitStatus | null | undefined, nowMs = Date.now()): LimitLevel => {
  if (!limit || isDrivingLimitStale(limit.calculated_at, nowMs)) return 'unknown';
  if (limit.status === 'reached') return 'reached';
  if (limit.status === 'warning') return 'warning';
  return 'ok';
};

export const weekLevel = (week: WeeklyDrivingLimit | null | undefined): LimitLevel =>
  week && week.limit_seconds > 0 ? levelForRatio(week.driven_seconds / week.limit_seconds) : 'unknown';

export const fourMonthLevel = (avg: FourMonthAverageLimit | null | undefined): LimitLevel =>
  avg && avg.limit_weekly_seconds > 0
    ? levelForRatio(avg.average_weekly_seconds / avg.limit_weekly_seconds)
    : 'unknown';

export const limitLevels = (limits: DriverLimits, nowMs = Date.now()): Record<LimitKey, LimitLevel> => ({
  rolling24h: rolling24hLevel(limits.rolling24h, nowMs),
  week: weekLevel(limits.week),
  fourMonth: fourMonthLevel(limits.fourMonth),
});

/** The limit closest to running out, or null when every limit is ok or unknown. */
export const mostUrgentLimit = (levels: Record<LimitKey, LimitLevel>): LimitKey | null => {
  // Ties go to the shortest horizon: it is the one the driver can act on now.
  const order: LimitKey[] = ['rolling24h', 'week', 'fourMonth'];
  let best: LimitKey | null = null;
  for (const key of order) {
    if (LEVEL_RANK[levels[key]] < LEVEL_RANK.warning) continue;
    if (!best || LEVEL_RANK[levels[key]] > LEVEL_RANK[levels[best]]) best = key;
  }
  return best;
};

export const progressRatio = (used: number, limit: number): number =>
  limit > 0 ? Math.min(1, Math.max(0, used / limit)) : 0;

/** "44,5" in Portuguese, "44.5" in English; whole numbers without decimals. */
export const formatHoursDecimal = (seconds: number, language?: string): string => {
  const hours = Math.round((seconds / 3600) * 10) / 10;
  return formatLimitHours(hours * 3600, language);
};

/** ALL-174: true when the server counted the 10 h per calendar day. */
export const isCalendarDay = (limit: DrivingLimitStatus | null | undefined): boolean =>
  limit?.mode === 'calendar_day';

/** Title of the 10 h row: «últimas 24 h» or «hoje (00:00–24:00)». */
export const rolling24hTitleKey = (limit: DrivingLimitStatus | null | undefined): string =>
  isCalendarDay(limit) ? 'workTime.limits.calendarDay.title' : 'workTime.limits.rolling24h.title';

/**
 * The text of the existing 10 h notice (DrivingLimitNotice, ALL-41), with the
 * same keys and conditions, so the notice can move into the limits card
 * without changing its behaviour. Null when the notice would render nothing.
 */
export const drivingLimitNoticeMessage = (
  limit: DrivingLimitStatus | null | undefined,
  t: Translate,
  language?: string,
  nowMs = Date.now(),
): string | null => {
  if (!limit || limit.status === 'ok' || isDrivingLimitStale(limit.calculated_at, nowMs)) return null;
  const hours = formatLimitHours(limit.limit_seconds, language);
  const reached = limit.status === 'reached';
  const time =
    reached && !limit.is_driving && limit.below_limit_again_at ? formatClock(limit.below_limit_again_at) : null;
  if (isCalendarDay(limit)) {
    // ALL-174 per-day mode: the count restarts at 00:00, so the reset time is
    // shown even while driving.
    const resets = limit.resets_at ?? limit.below_limit_again_at;
    return reached
      ? t('drivingLimit.day.noticeReached', { hours, time: resets ? formatClock(resets) : '00:00' })
      : t('drivingLimit.day.noticeWarning', { remaining: formatDrivingDuration(limit.remaining_seconds), hours });
  }
  return reached
    ? time
      ? t('drivingLimit.noticeReachedUntil', { hours, time })
      : t('drivingLimit.noticeReached', { hours })
    : t('drivingLimit.noticeWarning', { remaining: formatDrivingDuration(limit.remaining_seconds), hours });
};

/**
 * Compact duration for the home summary, read without maths: "3 h 18",
 * "31 h", "45 min". Minutes are floored, like formatDrivingDuration.
 */
export const formatShortDuration = (seconds: number): string => {
  const totalMinutes = Math.max(0, Math.floor(seconds / 60));
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${String(m).padStart(2, '0')}`;
};

/** H:MM:SS for the live state timer. */
export const formatTimer = (seconds: number): string => {
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};
